import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { type FieldEngineerSyncResult, syncCompanyFieldEngineers } from '../../api/sync-company-field-engineers.js';

/**
 * "Sync field engineers from ServiceNow" for one partner company: an explanation in plain words, one button, a
 * progress indicator while it runs, and a report of what was found and what changed once it is done.
 *
 * Rendered only for people who may use the FE Import / Export (the section is gated in the entity config). What the
 * sync does is described in api/sync-company-field-engineers.ts; the text below is the non-technical version of it.
 */

const STATUS_LABEL: Record<string, { label: string; badge: string }> = {
  updated: { label: 'Updated', badge: 'badge-success' },
  unchanged: { label: 'Already up to date', badge: 'badge-ghost' },
  missing: { label: 'Not in SmartTeams', badge: 'badge-warning' },
  failed: { label: 'Needs attention', badge: 'badge-error' },
};

const summaryTile = (value: number, label: string, tone: string) => html`
  <div class="flex flex-col items-center justify-center rounded-box border border-base-300 px-4 py-2 min-w-28">
    <span class="text-2xl font-semibold ${tone}">${value}</span>
    <span class="text-xs text-base-content/70 text-center">${label}</span>
  </div>
`;

const report = (result: FieldEngineerSyncResult) => {
  const rows = result.rows ?? [];
  const found = result.found ?? 0;
  const clientName = result.clientName ?? 'this ServiceNow company';

  if (found === 0) {
    return html`
      <div class="alert alert-warning text-sm py-2" role="status">
        <span class="icon-[mdi--alert-outline] text-base shrink-0"></span>
        <span>
          ServiceNow lists no field engineers under <strong>${clientName}</strong>, so nothing was changed. Engineers are
          recognised by a ServiceNow group whose name contains "${clientName}".
        </span>
      </div>
    `;
  }

  return html`
    <div class="flex flex-col gap-3">
      <p class="text-sm">
        Sync finished. ServiceNow lists <strong>${found}</strong> field engineer${found === 1 ? '' : 's'} under
        <strong>${clientName}</strong>.
      </p>
      <div class="flex flex-wrap gap-2">
        ${summaryTile(result.updated ?? 0, 'Updated', 'text-success')}
        ${summaryTile(result.unchanged ?? 0, 'Already up to date', 'text-base-content')}
        ${summaryTile(result.missing ?? 0, 'Not in SmartTeams yet', 'text-warning')}
        ${summaryTile(result.failed ?? 0, 'Need attention', 'text-error')}
      </div>
      ${(result.missing ?? 0) > 0
        ? html`<p class="text-xs text-base-content/70">
            Engineers marked "Not in SmartTeams" were not created. Add them with FE Import / Export, then run this sync
            again.
          </p>`
        : nothing}
      <!-- Stacked rows rather than a table: the detail panel can be narrow, and three columns would clip. -->
      <ul class="overflow-y-auto max-h-96 rounded-box border border-base-300 divide-y divide-base-300">
        ${rows.map(row => {
          const status = STATUS_LABEL[row.status ?? ''] ?? { label: row.status ?? '', badge: 'badge-ghost' };
          return html`
            <li class="flex flex-col gap-0.5 px-3 py-2">
              <div class="flex items-start justify-between gap-2">
                ${row.fieldEngineerId
                  ? html`<a
                      class="font-medium link link-hover"
                      href="/field-engineers/${row.fieldEngineerId}"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open ${row.name} in a new tab"
                      >${row.name}</a
                    >`
                  : html`<span class="font-medium">${row.name}</span>`}
                <span class="badge badge-sm shrink-0 whitespace-nowrap ${status.badge}">${status.label}</span>
              </div>
              ${row.email ? html`<span class="text-xs text-base-content/60 break-all">${row.email}</span>` : nothing}
              <span class="text-sm">${row.details}</span>
            </li>
          `;
        })}
      </ul>
    </div>
  `;
};

export const FieldEngineerSync = stateful(
  ({ companyId, clientLinked }: { companyId: string | undefined; clientLinked: boolean }) => {
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState<FieldEngineerSyncResult | null>(null);
    const [error, setError] = useState('');

    const run = async () => {
      if (!companyId || running) return;
      setRunning(true);
      setError('');
      setResult(null);
      try {
        const synced = await syncCompanyFieldEngineers(companyId)({
          _id: 1,
          clientName: 1,
          found: 1,
          updated: 1,
          unchanged: 1,
          missing: 1,
          failed: 1,
          rows: { _id: 1, name: 1, email: 1, status: 1, details: 1, fieldEngineerId: 1 },
        });
        setResult(synced);
      } catch (e) {
        setError(e instanceof Error && e.message ? e.message : 'The sync could not be completed. Please try again.');
      } finally {
        setRunning(false);
      }
    };

    const blocker = !companyId
      ? 'Save the company first. The sync becomes available once it has been created.'
      : !clientLinked
        ? 'Choose a ServiceNow company in the "Partner company information" section above, save, and then come back here.'
        : '';

    return html`
      <div class="flex flex-col gap-4">
        <div class="alert alert-info text-sm py-2 items-start" role="note">
          <span class="icon-[mdi--information-outline] text-base shrink-0 mt-0.5"></span>
          <div class="flex flex-col gap-1">
            <span class="font-medium">What this button does</span>
            <span>
              It brings the field engineers you already have in SmartTeams up to date with ServiceNow. For every engineer
              that ServiceNow lists under the ServiceNow company chosen above, it:
            </span>
            <ul class="list-disc pl-5">
              <li>
                adds this company to the engineer (an engineer who works for several companies gets each one when you
                run the sync from that company);
              </li>
              <li>
                sets the engineer as a partner or an internal engineer, as ServiceNow says (if ServiceNow cannot tell,
                the current setting is kept);
              </li>
              <li>links the engineer to their ServiceNow account, if that was missing.</li>
            </ul>
            <span>
              No company is ever removed from an engineer and nobody is deleted. Engineers who are not listed under that
              ServiceNow company are left alone, and new engineers are not created here (use FE Import / Export for
              that).
            </span>
          </div>
        </div>

        ${blocker
          ? html`<div class="alert alert-warning text-sm py-2" role="status">
              <span class="icon-[mdi--alert-outline] text-base shrink-0"></span>
              <span>${blocker}</span>
            </div>`
          : nothing}

        <div class="flex items-center gap-3 flex-wrap">
          <button class="btn btn-primary" ?disabled=${running || !!blocker} @click=${run}>
            ${running
              ? html`<span class="loading loading-spinner loading-sm"></span>`
              : html`<span class="icon-[mdi--sync] text-lg"></span>`}
            ${running ? 'Syncing…' : 'Sync field engineers now'}
          </button>
          ${running
            ? html`<span class="text-sm text-base-content/70" role="status">
                Asking ServiceNow and updating the engineers. This can take up to a minute. Please keep this page open.
              </span>`
            : nothing}
        </div>

        ${error
          ? html`<div class="alert alert-error text-sm py-2" role="alert">
              <span class="icon-[mdi--alert-circle-outline] text-base shrink-0"></span>
              <span>${error}</span>
            </div>`
          : nothing}
        ${result ? report(result) : nothing}
      </div>
    `;
  },
);
